import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock } from 'lucide-react';
import { useSeo } from '@/lib/seo';
import { fmtDate } from '@/lib/utils';

function readingTime(html) {
  const text = String(html || '').replace(/<[^>]+>/g, ' ');
  const words = text.split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
}

export default function BlogArticle() {
  const { slug } = useParams();

  const { data: post, isLoading } = useQuery({
    queryKey: ['blog-post', slug],
    queryFn: async () => {
      const all = await base44.entities.BlogPost.filter({ published: true }, '-published_date', 200);
      return all.find((p) => p.slug === slug);
    },
  });

  const { data: related = [] } = useQuery({
    queryKey: ['blog-related', post?.category, post?.id],
    queryFn: async () => {
      if (!post?.category) return [];
      const all = await base44.entities.BlogPost.filter({ published: true, category: post.category }, '-published_date', 10);
      return all.filter((p) => p.id !== post.id).slice(0, 3);
    },
    enabled: !!post?.category,
  });

  useSeo({
    title: post?.seo_title || post?.title,
    description: post?.seo_description || post?.excerpt,
    image: post?.cover_image,
    type: 'article',
    jsonLd: post ? {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: post.title,
      description: post.seo_description || post.excerpt || '',
      image: post.cover_image ? [post.cover_image] : undefined,
      datePublished: post.published_date,
      dateModified: post.updated_date || post.published_date,
      author: { '@type': 'Organization', name: post.author_name || 'Motion Reality' },
      publisher: { '@type': 'Organization', name: 'Motion Reality' },
      mainEntityOfPage: { '@type': 'WebPage', '@id': window.location.href },
    } : undefined,
  });

  if (isLoading) {
    return <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  }
  if (!post) {
    return <div className="min-h-screen bg-[#0d0d0f] pt-16 flex flex-col items-center justify-center gap-4"><p className="text-white/30 text-sm">Article not found</p><Link to="/blog" className="text-primary text-sm flex items-center gap-2 hover:underline"><ArrowLeft className="w-4 h-4" /> Back to Blog</Link></div>;
  }

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      <div className="border-b border-white/5 px-6 lg:px-10 py-4">
        <div className="max-w-3xl mx-auto"><Link to="/blog" className="inline-flex items-center gap-2 text-white/30 hover:text-white text-sm transition-colors"><ArrowLeft className="w-4 h-4" /> Back to Blog</Link></div>
      </div>

      <article className="max-w-3xl mx-auto px-6 lg:px-10 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <p className="text-primary text-xs font-bold tracking-widest mb-3">{post.category?.toUpperCase()}</p>
          <h1 className="text-white font-black text-3xl md:text-5xl uppercase leading-tight mb-5">{post.title}</h1>
          <div className="flex flex-wrap items-center gap-4 text-white/40 text-xs font-bold tracking-widest mb-8 pb-8 border-b border-white/5">
            <span>{post.author_name || 'MOTION REALITY'}</span>
            {post.published_date && <span>{fmtDate(post.published_date)}</span>}
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {readingTime(post.body_html)} MIN READ</span>
          </div>
        </motion.div>

        {post.cover_image && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="aspect-[16/9] bg-[#111115] overflow-hidden mb-10">
            <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
          </motion.div>
        )}

        <div
          className="text-white/70 text-base leading-relaxed
            [&_h2]:text-white [&_h2]:font-bold [&_h2]:text-2xl [&_h2]:uppercase [&_h2]:tracking-tight [&_h2]:mt-10 [&_h2]:mb-4
            [&_h3]:text-white [&_h3]:font-bold [&_h3]:text-lg [&_h3]:mt-6 [&_h3]:mb-3
            [&_p]:mb-4 [&_a]:text-primary [&_a]:underline
            [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-4
            [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-4
            [&_img]:w-full [&_img]:rounded [&_img]:my-6
            [&_blockquote]:border-l-2 [&_blockquote]:border-primary [&_blockquote]:pl-4 [&_blockquote]:text-white/60 [&_blockquote]:italic [&_blockquote]:my-4"
          dangerouslySetInnerHTML={{ __html: post.body_html || '' }}
        />

        {post.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-10 pt-8 border-t border-white/5">
            {post.tags.map((t) => (
              <span key={t} className="text-white/40 text-[10px] font-bold tracking-widest border border-white/10 px-3 py-1">#{t.toUpperCase()}</span>
            ))}
          </div>
        )}
      </article>

      {related.length > 0 && (
        <section className="px-6 lg:px-10 py-16 border-t border-white/5">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-white font-black text-2xl uppercase mb-8">Related Articles</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {related.map((p) => (
                <Link key={p.id} to={`/blog/${p.slug}`} className="group block bg-[#111115] hover:bg-[#1a1a1f] transition-colors">
                  <div className="aspect-[16/10] bg-[#0d0d0f] overflow-hidden">
                    {p.cover_image && <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400" />}
                  </div>
                  <div className="p-5">
                    <p className="text-primary text-[10px] font-bold tracking-widest mb-2">{p.category?.toUpperCase()}</p>
                    <h3 className="text-white font-bold text-base leading-snug line-clamp-2 group-hover:text-primary">{p.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}